import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  Legend,
} from 'recharts';
import {
  BarChart3,
  Globe2,
  Clock,
  Users,
  RefreshCw,
  LogOut,
  Shield,
  FileText,
  Download,
  AlertTriangle,
  Activity,
  LayoutGrid,
  MapPin,
  Languages,
  GripVertical,
  Filter,
} from 'lucide-react';
import ThemeToggle from '../components/ThemeToggle';
import AppFooter from '../components/AppFooter';
import { useTheme } from '../contexts/ThemeContext';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000';
const STORAGE_USER = 'invo_admin_dashboard_user';
const STORAGE_PASS = 'invo_admin_dashboard_password';
const STORAGE_WIDGET_ORDER = 'invo_admin_widget_order';

const WIDGET_IDS = ['kpis', 'trends', 'funnel', 'bars', 'geo', 'daily', 'hourly', 'heartbeat'];

const CHART_PRIMARY = '#edff00';
const CHART_ACCENT = '#c1f17e';
/** Axis / legend — tuned for dark chart surfaces (WCAG 1.4.3). Light theme uses `chartPalette.axis` instead. */
const CHART_MUTED = '#d4d4d8';

function useChartPalette(isLight) {
  return useMemo(() => {
    if (isLight) {
      return {
        axis: '#374151',
        grid: 'rgba(15, 23, 42, 0.1)',
        primary: '#4d5a06',
        accent: '#0f766e',
        success: '#15803d',
        danger: '#b91c1c',
        tooltipBg: '#ffffff',
        tooltipFg: '#1f2937',
        tooltipBorder: '#e5e7eb',
        tooltipLabel: '#374151',
        sparkPv: '#4d5a06',
        sparkUv: '#0f766e',
        sparkOk: '#15803d',
        sparkFail: '#b91c1c',
        sparkAtt: '#6d28d9',
        barPrimary: '#4d5a06',
        barAccent: '#0f766e',
      };
    }
    return {
      axis: CHART_MUTED,
      grid: 'rgba(255,255,255,0.08)',
      primary: CHART_PRIMARY,
      accent: CHART_ACCENT,
      success: '#22c55e',
      danger: '#ef4444',
      tooltipBg: '#0E141B',
      tooltipFg: CHART_MUTED,
      tooltipBorder: 'rgba(255,255,255,0.2)',
      tooltipLabel: CHART_MUTED,
      sparkPv: CHART_PRIMARY,
      sparkUv: CHART_ACCENT,
      sparkOk: '#22c55e',
      sparkFail: '#ef4444',
      sparkAtt: CHART_ACCENT,
      barPrimary: CHART_PRIMARY,
      barAccent: CHART_ACCENT,
    };
  }, [isLight]);
}

const focusRing =
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-background';

const brand = {
  logoSurface: { background: 'rgba(167, 238, 67, 0.15)', border: '1px solid rgba(167, 238, 67, 0.35)' },
  navPillIdle:
    'border-transparent text-foreground hover:border-primary/40 hover:bg-primary/[0.07] [data-theme="light"]:text-foreground',
  navPillActive:
    'border-primary/50 text-primary bg-[rgba(167,238,67,0.12)] shadow-[inset_0_1px_0_rgba(255,255,255,0.08),0_1px_2px_rgba(0,0,0,0.12)] backdrop-blur-xl [data-theme="light"]:bg-[rgba(237,255,0,0.18)]',
  panel:
    'rounded-2xl border border-border bg-card text-card-foreground shadow-[0_8px_32px_rgba(0,0,0,0.22)] [data-theme="light"]:shadow-[0_8px_32px_rgba(0,0,0,0.06)]',
  panelInset: 'rounded-xl border border-border/90 bg-bg-secondary/90 text-foreground backdrop-blur-sm',
  panelCompact:
    'rounded-xl border border-border bg-card text-card-foreground shadow-[0_8px_32px_rgba(0,0,0,0.22)] [data-theme="light"]:shadow-[0_8px_32px_rgba(0,0,0,0.06)] p-2.5 sm:p-3',
};

function readWidgetOrder() {
  try {
    const raw = localStorage.getItem(STORAGE_WIDGET_ORDER);
    if (!raw) return [...WIDGET_IDS];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [...WIDGET_IDS];
    const merged = [...new Set([...parsed.filter((id) => WIDGET_IDS.includes(id)), ...WIDGET_IDS])];
    return merged.filter((id, i) => merged.indexOf(id) === i);
  } catch {
    return [...WIDGET_IDS];
  }
}

function writeWidgetOrder(order) {
  try {
    localStorage.setItem(STORAGE_WIDGET_ORDER, JSON.stringify(order));
  } catch {
    /* ignore */
  }
}

/** Collapse consecutive daily+hourly into one row on wide screens (less vertical scroll). */
function expandWidgetOrderToSegments(order) {
  const segments = [];
  let i = 0;
  while (i < order.length) {
    const a = order[i];
    const b = order[i + 1];
    if (
      (a === 'daily' && b === 'hourly') ||
      (a === 'hourly' && b === 'daily')
    ) {
      segments.push({ type: 'pair', ids: [a, b] });
      i += 2;
    } else {
      segments.push({ type: 'single', id: a });
      i += 1;
    }
  }
  return segments;
}

function basicAuthHeader(username, password) {
  const raw = `${username}:${password}`;
  const bytes = new TextEncoder().encode(raw);
  let binary = '';
  for (let i = 0; i < bytes.length; i += 1) {
    binary += String.fromCharCode(bytes[i]);
  }
  return `Basic ${btoa(binary)}`;
}

function readStoredAuth() {
  try {
    const u = sessionStorage.getItem(STORAGE_USER);
    const p = sessionStorage.getItem(STORAGE_PASS);
    if (u && p) return { username: u, password: p };
  } catch {
    /* ignore */
  }
  return null;
}

function writeStoredAuth(username, password) {
  try {
    sessionStorage.setItem(STORAGE_USER, username);
    sessionStorage.setItem(STORAGE_PASS, password);
  } catch {
    /* ignore */
  }
}

function clearStoredAuth() {
  try {
    sessionStorage.removeItem(STORAGE_USER);
    sessionStorage.removeItem(STORAGE_PASS);
  } catch {
    /* ignore */
  }
}

function formatDurationSeconds(sec) {
  if (sec == null || Number.isNaN(sec)) return '—';
  if (sec < 60) return `${Math.round(sec)}s`;
  const m = Math.floor(sec / 60);
  const s = Math.round(sec % 60);
  return `${m}m ${s}s`;
}

function flagEmoji(code) {
  if (!code || typeof code !== 'string' || code.length !== 2) return '🌐';
  const u = code.toUpperCase();
  if (!/^[A-Z]{2}$/.test(u)) return '🌐';
  const A = 0x1f1e6;
  return String.fromCodePoint(A + u.charCodeAt(0) - 65, A + u.charCodeAt(1) - 65);
}

function startOfWeekMonday(d) {
  const x = new Date(d);
  const day = x.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  x.setDate(x.getDate() + diff);
  x.setHours(0, 0, 0, 0);
  return x;
}

function endOfWeekSunday(d) {
  const x = new Date(d);
  const day = x.getDay();
  const diff = day === 0 ? 0 : 7 - day;
  x.setDate(x.getDate() + diff);
  x.setHours(0, 0, 0, 0);
  return x;
}

function buildHeatmapMap(heatmap) {
  const m = {};
  (heatmap || []).forEach((h) => {
    m[h.date] = h.count;
  });
  return m;
}

function defaultCustomDates() {
  const to = new Date();
  const from = new Date();
  from.setDate(from.getDate() - 29);
  return { from: from.toISOString().slice(0, 10), to: to.toISOString().slice(0, 10) };
}

function DashboardSkeleton() {
  return (
    <div className="space-y-3 animate-pulse" aria-hidden>
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-2">
        {[1, 2, 3, 4, 5, 6].map((i) => (
          <div key={i} className="h-24 rounded-xl bg-card border border-border/60 shadow-[0_8px_32px_rgba(0,0,0,0.15)]" />
        ))}
      </div>
      <div className="h-32 rounded-xl bg-card border border-border/60 shadow-[0_8px_32px_rgba(0,0,0,0.15)]" />
      <div className="h-28 rounded-xl bg-card border border-border/60 shadow-[0_8px_32px_rgba(0,0,0,0.15)]" />
    </div>
  );
}

function SparklineMini({ data, color = CHART_PRIMARY, strokeWidth = 1 }) {
  const pts = (data || []).map((v, i) => ({ i, v: Number(v) || 0 }));
  if (pts.length === 0) return <div className="h-7 w-full rounded-md bg-background/40 border border-border/50" aria-hidden />;
  return (
    <div className="h-7 w-full mt-1 -mx-0.5" aria-hidden>
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={pts} margin={{ top: 1, right: 1, left: 1, bottom: 0 }}>
          <Line type="monotone" dataKey="v" stroke={color} strokeWidth={strokeWidth} dot={false} isAnimationActive={false} />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

function ContributionCalendar({ heatmap, from, to }) {
  const { isLight } = useTheme();
  const heatmapMap = useMemo(() => buildHeatmapMap(heatmap), [heatmap]);
  const weeks = useMemo(() => {
    const fromDay = String(from).slice(0, 10);
    const toDay = String(to).slice(0, 10);
    const rangeStart = new Date(`${fromDay}T12:00:00`);
    const rangeEnd = new Date(`${toDay}T12:00:00`);
    const gridStart = startOfWeekMonday(rangeStart);
    const gridEnd = endOfWeekSunday(rangeEnd);
    const wks = [];
    const cur = new Date(gridStart);
    while (cur <= gridEnd) {
      const week = [];
      for (let i = 0; i < 7; i += 1) {
        const key = cur.toISOString().slice(0, 10);
        const inRange = key >= fromDay && key <= toDay;
        week.push({ date: key, count: heatmapMap[key] || 0, inRange });
        cur.setDate(cur.getDate() + 1);
      }
      wks.push(week);
    }
    return wks;
  }, [from, to, heatmapMap]);

  const max = useMemo(() => Math.max(1, ...weeks.flat().map((c) => c.count)), [weeks]);
  const rowLabels = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

  return (
    <div className="space-y-2" role="region" aria-label="Daily activity by date">
      <div className="flex gap-2 items-start">
        <div className="flex flex-col gap-1 text-xs text-muted-foreground w-11 shrink-0 text-right leading-tight pt-0.5 font-medium">
          {rowLabels.map((label) => (
            <span key={label} className="h-3.5 flex items-center justify-end">
              {label}
            </span>
          ))}
        </div>
        <div className="overflow-x-auto pb-1 -mx-1 px-1">
          <div className="flex gap-1 min-w-max">
            {weeks.map((week, wi) => (
              <div key={wi} className="flex flex-col gap-1">
                {week.map((day) => {
                  const c = day.count;
                  const intensity = c / max;
                  const base = 'w-3.5 h-3.5 rounded-sm border transition-colors ring-1 ring-inset ';
                  let cell = 'border-white/10 ring-black/20 ';
                  if (!day.inRange) cell += 'opacity-30 bg-muted/25 border-border/50';
                  else if (c === 0) cell += isLight ? 'bg-bg-secondary/80 border-neutral-300' : 'bg-bg-secondary/80 border-white/10';
                  else if (isLight) {
                    if (intensity < 0.2) cell += 'bg-[#4d5a06]/25 border-[#4d5a06]/45';
                    else if (intensity < 0.4) cell += 'bg-[#4d5a06]/45 border-[#4d5a06]/55';
                    else if (intensity < 0.65) cell += 'bg-[#4d5a06]/75 border-[#3d4805]';
                    else cell += 'bg-[#3d4805] border-[#2a3204]';
                  } else if (intensity < 0.2) cell += 'bg-primary/20 border-primary/35';
                  else if (intensity < 0.4) cell += 'bg-primary/40 border-primary/45';
                  else if (intensity < 0.65) cell += 'bg-primary/75 border-primary/60';
                  else cell += 'bg-primary border-primary';
                  return <div key={day.date} title={`${day.date}: ${c}`} className={base + cell} />;
                })}
              </div>
            ))}
          </div>
        </div>
      </div>
      <p className="text-sm text-muted-foreground leading-snug">UTC · all events</p>
    </div>
  );
}

function WeekdayHourHeatmap({ data }) {
  const { isLight } = useTheme();
  const matrix = data?.matrix;
  const rowLabels = data?.row_labels || ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
  const max = Math.max(1, data?.max || 1);
  if (!matrix || !Array.isArray(matrix)) return null;
  const hours = Array.from({ length: 24 }, (_, i) => i);
  return (
    <div className="space-y-2 overflow-x-auto" role="region" aria-label="Activity by weekday and hour">
      <div className="flex gap-0.5 min-w-max pl-12">
        <div className="w-10 shrink-0" />
        {hours.map((h) => (
          <div key={h} className="w-5 sm:w-6 text-center text-xs text-muted-foreground tabular-nums shrink-0 leading-none">
            {h % 4 === 0 ? h : ''}
          </div>
        ))}
      </div>
      {matrix.map((row, di) => (
        <div key={di} className="flex gap-0.5 items-center min-w-max">
          <div className="w-10 shrink-0 text-xs text-muted-foreground text-right pr-1.5 font-medium">{rowLabels[di]}</div>
          {row.map((cell, hi) => {
            const c = cell;
            const intensity = c / max;
            return (
              <div
                key={`${di}-${hi}`}
                title={`${rowLabels[di]} ${hi}:00 — ${c}`}
                className={`w-5 h-5 sm:w-6 sm:h-6 rounded-sm border shrink-0 ring-1 ring-inset ring-black/10 ${
                  c === 0
                    ? isLight
                      ? 'bg-bg-secondary/90 border-neutral-300'
                      : 'bg-bg-secondary/90 border-white/10'
                    : isLight
                      ? intensity < 0.25
                        ? 'bg-[#0f766e]/30 border-[#0f766e]/50'
                        : intensity < 0.5
                          ? 'bg-[#0f766e]/55 border-[#0d6660]'
                          : intensity < 0.75
                            ? 'bg-[#0f766e]/85 border-[#0b524d]'
                            : 'bg-[#0d9488] border-[#0f766e]'
                      : intensity < 0.25
                        ? 'bg-accent/35 border-accent/50'
                        : intensity < 0.5
                          ? 'bg-accent/60 border-accent/55'
                          : intensity < 0.75
                            ? 'bg-accent/85 border-accent/70'
                            : 'bg-accent border-accent'
                }`}
              />
            );
          })}
        </div>
      ))}
      <p className="text-sm text-muted-foreground leading-snug">Server time</p>
    </div>
  );
}

function SectionHeader({ icon: Icon, title, subtitle, compact }) {
  return (
    <div className={`${compact ? 'mb-2 pb-1.5' : 'mb-3 pb-2'} border-b border-border/60`}>
      <div className="flex items-center gap-2">
        <div className="shrink-0 w-8 h-8 rounded-full flex items-center justify-center" style={brand.logoSurface}>
          <Icon className="w-[18px] h-[18px] text-primary" strokeWidth={2} aria-hidden />
        </div>
        <div className="min-w-0 flex-1">
          <h2 className={`${compact ? 'text-lg sm:text-xl' : 'text-xl'} font-heading font-bold text-foreground leading-snug`}>
            {title}
          </h2>
          {subtitle ? (
            <p className="text-sm text-muted-foreground leading-snug mt-1 line-clamp-2">{subtitle}</p>
          ) : null}
        </div>
      </div>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, hint, spark, sparkColor, sparkStrokeWidth = 1 }) {
  return (
    <div className={`${brand.panel} p-3 sm:p-3.5 rounded-xl transition-all duration-200 hover:border-primary/30`}>
      <div className="flex items-start justify-between gap-2">
        <div className="space-y-1 min-w-0 flex-1">
          <p className="text-sm font-semibold uppercase tracking-wide text-muted-foreground leading-snug">{label}</p>
          <p className="text-2xl sm:text-3xl font-heading font-bold tabular-nums text-foreground leading-tight truncate">
            {value ?? '—'}
          </p>
          {spark ? <SparklineMini data={spark} color={sparkColor ?? CHART_PRIMARY} strokeWidth={sparkStrokeWidth} /> : null}
          {hint ? <p className="text-sm text-muted-foreground leading-snug line-clamp-2">{hint}</p> : null}
        </div>
        <div
          className="shrink-0 w-10 h-10 rounded-full flex items-center justify-center text-primary border border-primary/25"
          style={{ background: 'rgba(167, 238, 67, 0.08)' }}
          aria-hidden
        >
          <Icon className="w-5 h-5" strokeWidth={2} />
        </div>
      </div>
    </div>
  );
}

function HeartbeatTable({ rows }) {
  const [sort, setSort] = useState({ key: 'seconds', dir: 'desc' });
  const [q, setQ] = useState('');
  const [page, setPage] = useState(1);
  const perPage = 8;

  const processed = useMemo(() => {
    let list = [...(rows || [])];
    const qq = q.trim().toLowerCase();
    if (qq) {
      list = list.filter((r) => r.visitor_id.toLowerCase().includes(qq));
    }
    list.sort((a, b) => {
      const va = sort.key === 'seconds' ? a.seconds : a.visitor_id;
      const vb = sort.key === 'seconds' ? b.seconds : b.visitor_id;
      const mul = sort.dir === 'asc' ? 1 : -1;
      if (typeof va === 'number') return (va - vb) * mul;
      return String(va).localeCompare(String(vb)) * mul;
    });
    return list;
  }, [rows, sort, q]);

  const totalPages = Math.max(1, Math.ceil(processed.length / perPage));
  const pageSafe = Math.min(page, totalPages);
  const slice = processed.slice((pageSafe - 1) * perPage, pageSafe * perPage);

  useEffect(() => {
    setPage(1);
  }, [q, rows]);

  const toggle = (key) => {
    setSort((s) => ({
      key,
      dir: s.key === key && s.dir === 'desc' ? 'asc' : 'desc',
    }));
  };

  if (!rows || rows.length === 0) {
    return (
      <div className="rounded-lg border border-dashed border-border/70 bg-background/40 px-3 py-4 text-center">
        <p className="text-base font-medium text-foreground">No heartbeats in this range</p>
        <p className="text-sm text-muted-foreground mt-2 max-w-2xl mx-auto leading-relaxed">
          Tab must stay visible on the designer. Widen the date range or generate traffic.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <div className="flex flex-col sm:flex-row gap-1.5 sm:items-center sm:justify-between">
        <div className="relative">
          <Filter className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" aria-hidden />
          <input
            type="search"
            placeholder="Filter id…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className={`form-input pl-9 py-2.5 text-base min-h-[44px] bg-input border-border w-full sm:w-64 ${focusRing}`}
            aria-label="Filter heartbeats by visitor id"
          />
        </div>
        <p className="text-sm text-muted-foreground">
          {processed.length} rows · {pageSafe}/{totalPages}
        </p>
      </div>
      <div className="overflow-x-auto rounded-lg border border-border/70 bg-background/40 max-h-48 overflow-y-auto">
        <table className="w-full text-base">
          <thead className="sticky top-0 z-[1]">
            <tr className="text-left border-b border-border bg-card/95">
              <th className="p-0">
                <button
                  type="button"
                  className={`font-semibold text-foreground hover:text-primary text-base min-h-[44px] w-full text-left px-3 py-2 ${focusRing}`}
                  onClick={() => toggle('visitor_id')}
                >
                  Visitor {sort.key === 'visitor_id' ? (sort.dir === 'asc' ? '↑' : '↓') : ''}
                </button>
              </th>
              <th className="p-0">
                <button
                  type="button"
                  className={`font-semibold text-foreground hover:text-primary text-base min-h-[44px] w-full text-left px-3 py-2 ${focusRing}`}
                  onClick={() => toggle('seconds')}
                >
                  Time {sort.key === 'seconds' ? (sort.dir === 'asc' ? '↑' : '↓') : ''}
                </button>
              </th>
            </tr>
          </thead>
          <tbody>
            {slice.map((row) => (
              <tr key={row.visitor_id} className="border-b border-border/30 hover:bg-primary/[0.03]">
                <td className="py-2 px-3 font-mono text-sm text-foreground">{row.visitor_id.slice(0, 12)}…</td>
                <td className="py-2 px-3 tabular-nums text-foreground">{formatDurationSeconds(row.seconds)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {totalPages > 1 ? (
        <div className="flex gap-2 justify-end">
          <button
            type="button"
            disabled={pageSafe <= 1}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            className={`btn btn-ghost border border-border px-4 min-h-[44px] text-base ${focusRing} disabled:opacity-50`}
          >
            Prev
          </button>
          <button
            type="button"
            disabled={pageSafe >= totalPages}
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            className={`btn btn-ghost border border-border px-4 min-h-[44px] text-base ${focusRing} disabled:opacity-50`}
          >
            Next
          </button>
        </div>
      ) : null}
    </div>
  );
}

function reorderWidgets(list, fromIndex, toIndex) {
  const next = [...list];
  const [removed] = next.splice(fromIndex, 1);
  next.splice(toIndex, 0, removed);
  return next;
}

export default function AdminDashboard() {
  const { isLight } = useTheme();
  const chartPalette = useChartPalette(isLight);

  const adminPageUrl = `${window.location.origin}/admin`;
  const skipRangeFetch = useRef(false);

  const [booting, setBooting] = useState(true);
  const [showLoginModal, setShowLoginModal] = useState(true);
  const [authenticated, setAuthenticated] = useState(false);
  const [auth, setAuth] = useState(null);

  const [loginUser, setLoginUser] = useState('');
  const [loginPass, setLoginPass] = useState('');
  const [loginError, setLoginError] = useState('');

  const [dateMode, setDateMode] = useState('preset');
  const [days, setDays] = useState(30);
  const def = useMemo(() => defaultCustomDates(), []);
  const [customFrom, setCustomFrom] = useState(def.from);
  const [customTo, setCustomTo] = useState(def.to);
  /** Rolling window (hours), with hourly chart buckets */
  const [lastHours, setLastHours] = useState(24);
  const [bucketHours, setBucketHours] = useState(1);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [data, setData] = useState(null);

  const [widgetOrder, setWidgetOrder] = useState(() => readWidgetOrder());
  const [dragId, setDragId] = useState(null);

  const rangeKey = useMemo(() => {
    if (dateMode === 'custom') return `c:${customFrom}:${customTo}`;
    if (dateMode === 'rolling') return `r:${lastHours}:${bucketHours}`;
    return `p:${days}`;
  }, [dateMode, days, customFrom, customTo, lastHours, bucketHours]);

  const buildQuery = useCallback(() => {
    if (dateMode === 'custom' && customFrom && customTo) {
      return `from=${encodeURIComponent(customFrom)}&to=${encodeURIComponent(customTo)}`;
    }
    if (dateMode === 'rolling') {
      return `last_hours=${lastHours}&bucket_hours=${bucketHours}`;
    }
    return `days=${days}`;
  }, [dateMode, days, customFrom, customTo, lastHours, bucketHours]);

  const loadSummary = useCallback(
    async (cred) => {
      if (!cred) return { ok: false, message: 'Missing credentials.' };
      setLoading(true);
      setError('');
      try {
        const res = await fetch(`${API_BASE}/api/admin/analytics/summary?${buildQuery()}`, {
          headers: {
            Accept: 'application/json',
            Authorization: basicAuthHeader(cred.username, cred.password),
            'X-Admin-User': cred.username,
            'X-Admin-Password': cred.password,
          },
        });
        if (res.status === 401) {
          setData(null);
          const msg = 'Invalid username or password.';
          setError(msg);
          return { ok: false, message: msg };
        }
        if (res.status === 503) {
          setData(null);
          const msg =
            'API is not configured: set ADMIN_DASHBOARD_USER and ADMIN_DASHBOARD_PASSWORD on the API host (local: php-backend/.env then restart; production: your host env, e.g. Koyeb, then redeploy).';
          setError(msg);
          return { ok: false, message: msg };
        }
        if (!res.ok) {
          setData(null);
          const msg = `Request failed (${res.status}).`;
          setError(msg);
          return { ok: false, message: msg };
        }
        setData(await res.json());
        setError('');
        return { ok: true };
      } catch (e) {
        const msg = e.message || 'Network error';
        setData(null);
        setError(msg);
        return { ok: false, message: msg };
      } finally {
        setLoading(false);
      }
    },
    [buildQuery]
  );

  useEffect(() => {
    const stored = readStoredAuth();
    if (!stored) {
      setBooting(false);
      return;
    }
    (async () => {
      const result = await loadSummary(stored);
      setBooting(false);
      if (result.ok) {
        skipRangeFetch.current = true;
        setAuth(stored);
        setAuthenticated(true);
        setShowLoginModal(false);
      } else {
        clearStoredAuth();
      }
    })();
  }, []);

  useEffect(() => {
    if (!authenticated || !auth) return;
    if (skipRangeFetch.current) {
      skipRangeFetch.current = false;
      return;
    }
    loadSummary(auth);
  }, [authenticated, auth, rangeKey, loadSummary]);

  const handleModalLogin = async (e) => {
    e.preventDefault();
    setLoginError('');
    const cred = { username: loginUser.trim(), password: loginPass };
    if (!cred.username) {
      setLoginError('Enter username.');
      return;
    }
    const result = await loadSummary(cred);
    if (!result.ok) {
      setLoginError(result.message || 'Sign-in failed.');
      return;
    }
    skipRangeFetch.current = true;
    writeStoredAuth(cred.username, cred.password);
    setAuth(cred);
    setAuthenticated(true);
    setShowLoginModal(false);
    setLoginPass('');
  };

  const handleLogout = () => {
    clearStoredAuth();
    setAuth(null);
    setAuthenticated(false);
    setData(null);
    setShowLoginModal(true);
    setError('');
  };

  const handleRefresh = () => {
    if (auth) loadSummary(auth);
  };

  const onDragStart = (e, id) => {
    setDragId(id);
    e.dataTransfer.effectAllowed = 'move';
    try {
      e.dataTransfer.setData('text/plain', id);
    } catch {
      /* ignore */
    }
  };

  const onDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const onDrop = (e, targetId) => {
    e.preventDefault();
    const src = dragId || e.dataTransfer.getData('text/plain');
    setDragId(null);
    if (!src || src === targetId) return;
    setWidgetOrder((order) => {
      const from = order.indexOf(src);
      const to = order.indexOf(targetId);
      if (from < 0 || to < 0) return order;
      const next = reorderWidgets(order, from, to);
      writeWidgetOrder(next);
      return next;
    });
  };

  const periodLabel = useMemo(() => {
    if (!data) return '';
    const from = new Date(data.from);
    const to = new Date(data.to);
    if (data.last_hours != null) {
      return `Last ${data.last_hours}h · ${data.bucket_hours ?? 1}h buckets · ${from.toLocaleString()} — ${to.toLocaleString()}`;
    }
    return `${from.toLocaleDateString()} — ${to.toLocaleDateString()}`;
  }, [data]);

  const ts = useMemo(() => {
    if (!data) return [];
    if (Array.isArray(data.timeseries) && data.timeseries.length > 0) return data.timeseries;
    return data.timeseries_daily || [];
  }, [data]);

  const sparkPv = ts.map((d) => d.page_views);
  const sparkUv = ts.map((d) => d.unique_visitors);
  const sparkOk = ts.map((d) => d.pdf_success);
  const sparkFail = ts.map((d) => d.pdf_failure);
  const sparkAtt = ts.map((d) => d.pdf_attempt);

  const funnel = data?.funnel || {};
  const fv = funnel.sessions_page_view || 0;
  const funnelPct = (n) => (fv > 0 ? Math.round((n / fv) * 1000) / 10 : 0);

  const trendsSubtitle = data?.last_hours != null ? `Bucketed by ${data.bucket_hours ?? 1}h · rolling window` : 'Daily series';

  const chartData = useMemo(
    () =>
      ts.map((d) => ({
        ...d,
        label: d.label ?? (d.date?.length >= 10 ? d.date.slice(5, 10) : d.date),
      })),
    [ts]
  );

  const topPaths = data?.top_paths || [];
  const countries = data?.visitors_by_country || [];

  const renderWidget = (id) => {
    if (!data) return null;
    switch (id) {
      case 'kpis':
        return (
          <section key="kpis" className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-2">
            <StatCard icon={BarChart3} label="Page views" value={data.page_views} hint="Designer loads" spark={sparkPv} sparkColor={chartPalette.sparkPv} sparkStrokeWidth={isLight ? 1.75 : 1} />
            <StatCard icon={Users} label="Visitors" value={data.unique_visitors} hint="Distinct IDs" spark={sparkUv} sparkColor={chartPalette.sparkUv} sparkStrokeWidth={isLight ? 1.75 : 1} />
            <StatCard icon={Download} label="PDF OK" value={data.pdf_success} hint="API success" spark={sparkOk} sparkColor={chartPalette.sparkOk} sparkStrokeWidth={isLight ? 1.75 : 1} />
            <StatCard icon={AlertTriangle} label="PDF fail" value={data.pdf_failure} spark={sparkFail} sparkColor={chartPalette.sparkFail} sparkStrokeWidth={isLight ? 1.75 : 1} />
            <StatCard icon={FileText} label="PDF tries" value={data.pdf_attempt} hint="Client attempts" spark={sparkAtt} sparkColor={chartPalette.sparkAtt} sparkStrokeWidth={isLight ? 1.75 : 1} />
            <StatCard
              icon={Clock}
              label="Median→PDF"
              value={formatDurationSeconds(data.median_seconds_to_pdf_success)}
              hint="View→success"
            />
          </section>
        );
      case 'trends':
        return (
          <div key="trends" className={brand.panelCompact}>
            <SectionHeader icon={Activity} title="Trends" subtitle={trendsSubtitle} compact />
            {chartData.length === 0 ? (
              <p className="text-base text-muted-foreground py-2">No data in range.</p>
            ) : (
              <div className="h-32 w-full mt-1">
                <ResponsiveContainer>
                  <AreaChart data={chartData} margin={{ top: 4, right: 4, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke={chartPalette.grid} />
                    <XAxis dataKey="label" tick={{ fontSize: 12, fill: chartPalette.axis }} height={18} />
                    <YAxis tick={{ fontSize: 12, fill: chartPalette.axis }} width={36} />
                    <Tooltip
                      contentStyle={{
                        background: chartPalette.tooltipBg,
                        border: `1px solid ${chartPalette.tooltipBorder}`,
                        borderRadius: 8,
                        fontSize: 14,
                        color: chartPalette.tooltipFg,
                      }}
                      labelStyle={{ color: chartPalette.tooltipLabel }}
                      itemStyle={{ color: chartPalette.tooltipFg }}
                    />
                    <Legend wrapperStyle={{ fontSize: 12, color: chartPalette.axis }} iconSize={11} />
                    <Area type="monotone" dataKey="page_views" name="Page views" stroke={chartPalette.primary} fill={chartPalette.primary} fillOpacity={isLight ? 0.22 : 0.15} />
                    <Area type="monotone" dataKey="unique_visitors" name="Unique visitors" stroke={chartPalette.accent} fill={chartPalette.accent} fillOpacity={isLight ? 0.18 : 0.12} />
                    <Area type="monotone" dataKey="pdf_success" name="PDF OK" stroke={chartPalette.success} fill={chartPalette.success} fillOpacity={isLight ? 0.16 : 0.1} />
                    <Area type="monotone" dataKey="pdf_failure" name="PDF fail" stroke={chartPalette.danger} fill={chartPalette.danger} fillOpacity={isLight ? 0.14 : 0.08} />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>
        );
      case 'funnel':
        return (
          <div key="funnel" className={brand.panelCompact}>
            <SectionHeader icon={Filter} title="Funnel" subtitle="Distinct sessions per stage" compact />
            <div className="grid grid-cols-2 xl:grid-cols-4 gap-2 mt-1">
              {[
                { k: 'sessions_page_view', label: 'Page view' },
                { k: 'sessions_pdf_attempt', label: 'PDF try' },
                { k: 'sessions_pdf_success', label: 'PDF OK' },
                { k: 'sessions_pdf_failure', label: 'PDF fail' },
              ].map((row) => {
                const n = funnel[row.k] ?? 0;
                const pct = funnelPct(n);
                return (
                  <div
                    key={row.k}
                    className="rounded-lg border border-border/70 bg-bg-secondary/95 text-foreground shadow-[inset_0_1px_0_rgba(255,255,255,0.04)] px-2.5 py-2"
                  >
                    <p className="text-sm uppercase tracking-wide text-muted-foreground">{row.label}</p>
                    <p className="text-2xl font-bold text-foreground tabular-nums leading-tight">{n}</p>
                    <p className="text-sm text-primary font-medium leading-snug">{fv > 0 ? `${pct}%` : '—'}</p>
                    <div className="mt-1.5 h-1.5 rounded-full bg-background/60 overflow-hidden border border-border/30">
                      <div
                        className={`h-full rounded-full transition-all ${isLight ? 'bg-[#4d5a06]' : 'bg-primary/80'}`}
                        style={{ width: `${Math.min(100, pct)}%` }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      case 'bars':
        return (
          <div key="bars" className="grid grid-cols-1 lg:grid-cols-2 gap-2">
            <div className={brand.panelCompact}>
              <SectionHeader icon={Globe2} title="Top countries" compact />
              {countries.length === 0 ? (
                <div className="rounded-lg border border-dashed border-border/70 px-2 py-3 text-center text-base text-muted-foreground leading-snug">
                  No geo yet — events need API IP resolution.
                </div>
              ) : (
                <div className="h-32 mt-1">
                  <ResponsiveContainer>
                    <BarChart data={countries.slice(0, 10)} layout="vertical" margin={{ left: 4, right: 4, top: 4, bottom: 4 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke={chartPalette.grid} />
                      <XAxis type="number" tick={{ fontSize: 12, fill: chartPalette.axis }} />
                      <YAxis type="category" dataKey="country_code" width={36} tick={{ fontSize: 12, fill: chartPalette.axis }} />
                      <Tooltip
                        contentStyle={{
                          background: chartPalette.tooltipBg,
                          border: `1px solid ${chartPalette.tooltipBorder}`,
                          borderRadius: 8,
                          color: chartPalette.tooltipFg,
                        }}
                        formatter={(v, name, p) => [v, p?.payload?.country_name || 'Visitors']}
                      />
                      <Bar dataKey="visitors" fill={chartPalette.barPrimary} radius={[0, 4, 4, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}
            </div>
            <div className={brand.panelCompact}>
              <SectionHeader icon={FileText} title="Top paths" compact />
              {topPaths.length === 0 ? (
                <p className="text-base text-muted-foreground py-2">No paths.</p>
              ) : (
                <div className="h-32 mt-1">
                  <ResponsiveContainer>
                    <BarChart data={topPaths.slice(0, 10)} layout="vertical" margin={{ left: 2, right: 4, top: 4, bottom: 4 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke={chartPalette.grid} />
                      <XAxis type="number" tick={{ fontSize: 12, fill: chartPalette.axis }} />
                      <YAxis
                        type="category"
                        dataKey="path"
                        width={88}
                        tick={{ fontSize: 12, fill: chartPalette.axis }}
                        tickFormatter={(v) => (v.length > 10 ? `${v.slice(0, 10)}…` : v)}
                      />
                      <Tooltip
                        contentStyle={{
                          background: chartPalette.tooltipBg,
                          border: `1px solid ${chartPalette.tooltipBorder}`,
                          borderRadius: 8,
                          color: chartPalette.tooltipFg,
                        }}
                      />
                      <Bar dataKey="count" fill={chartPalette.barAccent} radius={[0, 4, 4, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}
            </div>
          </div>
        );
      case 'geo':
        return (
          <div key="geo" className="grid grid-cols-1 lg:grid-cols-2 gap-2">
            <div className={brand.panelCompact}>
              <SectionHeader icon={Globe2} title="By country" compact />
              {(data.visitors_by_country || []).length === 0 ? (
                <div className="rounded-lg border border-dashed border-border/70 px-2 py-3 text-center text-base text-muted-foreground">
                  No IP→geo rows yet.
                </div>
              ) : (
                <ul className="space-y-1.5 max-h-36 overflow-y-auto pr-0.5 text-base">
                  {data.visitors_by_country.map((row) => (
                    <li
                      key={row.country_code}
                      className="flex items-center justify-between gap-1.5 rounded-lg border border-border/60 bg-card px-2.5 py-1.5 shadow-[0_2px_8px_rgba(0,0,0,0.12)]"
                    >
                      <span className="flex items-center gap-1.5 min-w-0">
                        <span className="text-base leading-none">{flagEmoji(row.country_code)}</span>
                        <span className="font-medium truncate">{row.country_name}</span>
                      </span>
                      <span className="tabular-nums font-bold text-primary text-base shrink-0">{row.visitors}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <div className={brand.panelCompact}>
              <SectionHeader icon={MapPin} title="TZ & locales" compact />
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-base">
                <div>
                  <p className="text-sm font-semibold uppercase text-muted-foreground mb-1">TZ</p>
                  {(data.timezone_distribution || []).length === 0 ? (
                    <p className="text-base text-muted-foreground">—</p>
                  ) : (
                    <ul className="space-y-1 max-h-32 overflow-y-auto">
                      {data.timezone_distribution.map((row) => (
                        <li key={row.timezone} className="flex justify-between gap-1 border-b border-border/25 pb-1.5 text-base">
                          <span className="truncate text-foreground">{row.timezone}</span>
                          <span className="font-semibold shrink-0">{row.visitors}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
                <div>
                  <p className="text-sm font-semibold uppercase text-muted-foreground mb-1 flex items-center gap-0.5">
                    <Languages className="w-4 h-4" aria-hidden /> Lang
                  </p>
                  {(data.locale_distribution || []).length === 0 ? (
                    <p className="text-base text-muted-foreground">—</p>
                  ) : (
                    <ul className="space-y-1 max-h-32 overflow-y-auto">
                      {data.locale_distribution.map((row) => (
                        <li key={row.locale} className="flex justify-between gap-1 border-b border-border/25 pb-1.5 text-base">
                          <span className="font-mono text-foreground">{row.locale}</span>
                          <span className="font-semibold shrink-0">{row.visitors}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            </div>
          </div>
        );
      case 'daily':
        return (
          <div key="daily" className={brand.panelCompact}>
            <SectionHeader icon={Activity} title="Daily heatmap" compact />
            <ContributionCalendar heatmap={data.heatmap} from={data.from} to={data.to} />
          </div>
        );
      case 'hourly':
        return (
          <div key="hourly" className={brand.panelCompact}>
            <SectionHeader icon={LayoutGrid} title="Weekday × hour" compact />
            <WeekdayHourHeatmap data={data.heatmap_weekday_hour} />
          </div>
        );
      case 'heartbeat':
        return (
          <div key="heartbeat" className={brand.panelCompact}>
            <SectionHeader icon={Clock} title="Heartbeats" compact />
            <HeartbeatTable rows={data.time_per_visitor_seconds} />
          </div>
        );
      default:
        return null;
    }
  };

  const widgetSegments = useMemo(() => expandWidgetOrderToSegments(widgetOrder), [widgetOrder]);

  const renderWidgetRow = (wid) => (
    <div
      key={wid}
      role="group"
      aria-label={`Widget ${wid}`}
      onDragOver={onDragOver}
      onDrop={(e) => onDrop(e, wid)}
      className={`rounded-xl border border-transparent ${dragId === wid ? 'ring-1 ring-primary/35' : ''}`}
    >
      <div className="flex gap-1 items-start">
        <button
          type="button"
          draggable
          onDragStart={(e) => onDragStart(e, wid)}
          onDragEnd={() => setDragId(null)}
          className={`mt-0.5 inline-flex items-center justify-center min-h-[44px] min-w-[44px] shrink-0 rounded-lg text-muted-foreground hover:text-foreground hover:bg-bg-secondary/80 cursor-grab active:cursor-grabbing ${focusRing}`}
          aria-label={`Drag to reorder ${wid}`}
        >
          <GripVertical className="w-5 h-5" aria-hidden />
        </button>
        <div className="flex-1 min-w-0">{renderWidget(wid)}</div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen flex flex-col bg-background text-foreground text-base leading-relaxed">
      <a
        href="#admin-dashboard-main"
        className="fixed left-4 top-4 z-[100] -translate-x-[120vw] focus:translate-x-0 rounded-lg bg-primary px-4 py-3 text-base font-semibold text-primary-foreground shadow-lg transition-transform focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-foreground focus-visible:ring-offset-2 focus-visible:ring-offset-primary"
      >
        Skip to dashboard content
      </a>
      {showLoginModal ? (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-[#0a0f14]/70 backdrop-blur-md p-4 [data-theme='light']:bg-black/40"
          role="dialog"
          aria-modal="true"
          aria-labelledby="admin-login-title"
        >
          <div className={`w-full max-w-md p-6 sm:p-7 ${brand.panel} border-primary/25`}>
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 rounded-full flex items-center justify-center shrink-0" style={brand.logoSurface}>
                <Shield className="w-5 h-5 text-primary" strokeWidth={2} aria-hidden />
              </div>
              <div>
                <h2 id="admin-login-title" className="text-2xl font-heading font-bold text-foreground">
                  Admin analytics
                </h2>
                <p id="admin-login-desc" className="text-sm text-muted-foreground">
                  Private — not linked from the invoice tool
                </p>
              </div>
            </div>
            <form onSubmit={handleModalLogin} className="space-y-3 mt-4" aria-describedby="admin-login-desc">
              <label className="block space-y-1.5">
                <span className="text-sm font-semibold text-foreground">Username</span>
                <input
                  type="text"
                  autoComplete="username"
                  value={loginUser}
                  onChange={(ev) => setLoginUser(ev.target.value)}
                  className={`form-input bg-input border-border text-base min-h-[44px] py-2.5 ${focusRing}`}
                />
              </label>
              <label className="block space-y-1.5">
                <span className="text-sm font-semibold text-foreground">Password</span>
                <input
                  type="password"
                  autoComplete="current-password"
                  value={loginPass}
                  onChange={(ev) => setLoginPass(ev.target.value)}
                  className={`form-input bg-input border-border text-base min-h-[44px] py-2.5 ${focusRing}`}
                />
              </label>
              {(loginError || error) ? (
                <p className="text-base text-destructive" role="alert">
                  {loginError || error}
                </p>
              ) : null}
              <button
                type="submit"
                disabled={loading || booting}
                className={`btn btn-primary w-full justify-center min-h-[44px] text-base font-semibold rounded-2xl ${focusRing}`}
              >
                {loading || booting ? 'Signing in…' : 'View analytics'}
              </button>
            </form>
          </div>
        </div>
      ) : null}

      <main id="admin-dashboard-main" className="flex-1 flex flex-col min-h-0" aria-label="Analytics dashboard">
      <div className="flex-1 px-2 sm:px-4 lg:px-5 pt-3 pb-6">
        <div className="max-w-[1700px] mx-auto space-y-3">
          <header className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-2 border-b border-border/70 pb-3">
            <div className="flex items-center gap-2 md:ml-1">
              <div className="w-9 h-9 rounded-full flex items-center justify-center shrink-0" style={brand.logoSurface}>
                <span className="font-semibold text-sm text-primary lowercase">invo</span>
              </div>
              <div className="min-w-0">
                <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground tracking-tight leading-tight">Analytics</h1>
                <p className="text-sm text-muted-foreground mt-1 truncate max-w-[min(100vw-6rem,42rem)]">
                  <code className="rounded bg-bg-secondary/90 px-1.5 py-0.5 font-mono text-sm text-primary border border-primary/20">
                    {adminPageUrl}
                  </code>
                </p>
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <ThemeToggle />
              {!showLoginModal && authenticated ? (
                <>
                  <button
                    type="button"
                    onClick={handleRefresh}
                    disabled={loading}
                    className={`inline-flex items-center justify-center gap-2 rounded-2xl px-4 min-h-[44px] text-sm font-medium border ${focusRing} ${loading ? brand.navPillActive : brand.navPillIdle}`}
                  >
                    <RefreshCw className={`w-4 h-4 shrink-0 ${loading ? 'animate-spin' : ''}`} aria-hidden />
                    Refresh
                  </button>
                  <button
                    type="button"
                    onClick={handleLogout}
                    className={`inline-flex items-center justify-center gap-2 rounded-2xl px-4 min-h-[44px] text-sm font-medium border border-border ${focusRing}`}
                  >
                    <LogOut className="w-4 h-4 shrink-0" aria-hidden />
                    Log out
                  </button>
                </>
              ) : null}
            </div>
          </header>

          {!showLoginModal && authenticated ? (
            <section className={`${brand.panelInset} p-2 sm:p-3 flex flex-col gap-2`}>
              <div className="flex flex-wrap gap-2 items-end">
                <label className="flex flex-col gap-1.5">
                  <span className="text-sm font-semibold text-foreground">Mode</span>
                  <select
                    value={dateMode}
                    onChange={(e) => setDateMode(e.target.value)}
                    className={`form-select bg-input text-base min-h-[44px] py-2 min-w-[180px] ${focusRing}`}
                  >
                    <option value="preset">Last N days</option>
                    <option value="rolling">Recent hours</option>
                    <option value="custom">Custom range</option>
                  </select>
                </label>
                {dateMode === 'rolling' ? (
                  <>
                    <label className="flex flex-col gap-1.5">
                      <span className="text-sm font-semibold text-foreground">Window</span>
                      <select
                        value={lastHours}
                        onChange={(e) => {
                          const h = Number(e.target.value);
                          setLastHours(h);
                          setBucketHours((prev) => {
                            const allowed = [1, 5, 10].filter((b) => b <= h);
                            return allowed.includes(prev) ? prev : allowed[allowed.length - 1];
                          });
                        }}
                        className={`form-select bg-input text-base min-h-[44px] py-2 ${focusRing}`}
                      >
                        <option value={1}>Last 1 hour</option>
                        <option value={5}>Last 5 hours</option>
                        <option value={10}>Last 10 hours</option>
                        <option value={24}>Last 24 hours</option>
                      </select>
                    </label>
                    <label className="flex flex-col gap-1.5">
                      <span className="text-sm font-semibold text-foreground">Chart buckets</span>
                      <select
                        value={bucketHours}
                        onChange={(e) => setBucketHours(Number(e.target.value))}
                        className={`form-select bg-input text-base min-h-[44px] py-2 ${focusRing}`}
                      >
                        {[1, 5, 10]
                          .filter((b) => b <= lastHours)
                          .map((b) => (
                            <option key={b} value={b}>
                              {b} hour{b === 1 ? '' : 's'}
                            </option>
                          ))}
                      </select>
                    </label>
                  </>
                ) : null}
                {dateMode === 'preset' ? (
                  <label className="flex flex-col gap-1.5">
                    <span className="text-sm font-semibold text-foreground">Preset</span>
                    <select
                      value={days}
                      onChange={(e) => setDays(Number(e.target.value))}
                      className={`form-select bg-input text-base min-h-[44px] py-2 ${focusRing}`}
                    >
                      {[7, 14, 30, 60, 90, 180, 365].map((d) => (
                        <option key={d} value={d}>
                          {d} days
                        </option>
                      ))}
                    </select>
                  </label>
                ) : (
                  <>
                    <label className="flex flex-col gap-1.5">
                      <span className="text-sm font-semibold text-foreground">From</span>
                      <input
                        type="date"
                        value={customFrom}
                        onChange={(e) => setCustomFrom(e.target.value)}
                        className={`form-input bg-input text-base min-h-[44px] py-2 w-[160px] ${focusRing}`}
                      />
                    </label>
                    <label className="flex flex-col gap-1.5">
                      <span className="text-sm font-semibold text-foreground">To</span>
                      <input
                        type="date"
                        value={customTo}
                        onChange={(e) => setCustomTo(e.target.value)}
                        className={`form-input bg-input text-base min-h-[44px] py-2 w-[160px] ${focusRing}`}
                      />
                    </label>
                  </>
                )}
                {periodLabel ? (
                  <p className="text-base text-muted-foreground flex-1 min-w-[200px]">
                    Range: <span className="text-foreground font-semibold">{periodLabel}</span>
                  </p>
                ) : null}
              </div>
              {error ? (
                <p className="text-base text-destructive font-medium" role="alert" aria-live="polite">
                  {error}
                </p>
              ) : null}
            </section>
          ) : null}

          {authenticated && !showLoginModal && loading && !data ? <DashboardSkeleton /> : null}

          {data && !showLoginModal ? (
            <div className="space-y-2">
              <p className="text-sm text-muted-foreground flex items-center gap-2">
                <GripVertical className="w-4 h-4 shrink-0 opacity-70" aria-hidden />
                Drag ⋮⋮ to reorder · saved locally
              </p>
              {widgetSegments.map((seg, si) =>
                seg.type === 'single' ? (
                  renderWidgetRow(seg.id)
                ) : (
                  <div
                    key={`heatmap-${seg.ids[0]}-${seg.ids[1]}-${si}`}
                    className="grid grid-cols-1 xl:grid-cols-2 gap-2"
                  >
                    {seg.ids.map((wid) => renderWidgetRow(wid))}
                  </div>
                )
              )}
            </div>
          ) : null}
        </div>
      </div>
      </main>

      <AppFooter />
    </div>
  );
}
